import { Button, Group, Paper, Stack, Text, Textarea } from "@mantine/core"
import { notifications } from "@dehub/theme"
import { PlusIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { IInboundException } from "../../apis/inbound-order"
import { formatQuantity } from "../../utils/format-quantity"
import { DeliveryActionButton } from "../delivery/action-controls"
import { translateBackendError } from "../delivery/translate-error"
import { InboundExceptionStatusBadge, InboundExceptionTypeBadge } from "./atoms"
import { useInboundExceptionMutations, useInboundMutations } from "./hooks"
import { useInboundActions } from "./inbound-actions"

type Props = Readonly<{
  inboundOrderId: string
  exceptions: IInboundException[] | undefined
  onRaise: () => void
}>

export function InboundExceptionsPanel({ inboundOrderId, exceptions, onRaise }: Props) {
  const { t } = useTranslation(["inbound", "common"])
  const { stateOf } = useInboundActions()
  const exMutations = useInboundExceptionMutations()
  const mutations = useInboundMutations()
  const [resolveTarget, setResolveTarget] = useState<string | null>(null)
  const [resolveNotes, setResolveNotes] = useState("")
  const [closeTarget, setCloseTarget] = useState<string | null>(null)
  const [closeNotes, setCloseNotes] = useState("")

  const attempt = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
    } catch {
      // shown through the hook's error
    }
  }

  const repostStock = () =>
    attempt(async () => {
      const r = await mutations.repostStock(inboundOrderId)
      notifications.show({ color: r.stockPosted ? "teal" : "red", message: r.stockPosted ? t("inbound:detail.repostDone") : r.stockError ? `${t("inbound:detail.repostFailed")} ${r.stockError}` : t("inbound:detail.repostFailed") })
    })

  const list = exceptions ?? []

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="center" mb="sm">
        <Text fz={14} fw={700} tt="uppercase" c="gray.8">
          {t("inbound:detail.exceptionsTitle")} {list.length > 0 ? `(${list.length})` : ""}
        </Text>
        <DeliveryActionButton state={stateOf("exceptionRaise", {})} variant="default" size="sm" leftSection={<PlusIcon size={14} />} onClick={onRaise}>
          {t("inbound:detail.newException")}
        </DeliveryActionButton>
      </Group>

      {list.length === 0 ? (
        <Text fz={14}>{t("inbound:detail.noExceptions")}</Text>
      ) : (
        <Stack gap="sm">
          {list.map((ex) => {
            const subject = { exceptionStatus: ex.status, exceptionType: ex.exceptionType }
            const start = stateOf("exceptionStart", subject)
            const resolve = stateOf("exceptionResolve", subject)
            const close = stateOf("exceptionClose", subject)
            const repost = stateOf("repostStock", subject)
            return (
              <Paper key={ex.id} withBorder radius="sm" p="sm">
                <Group justify="space-between" align="center" mb={6}>
                  <Group gap="sm">
                    <InboundExceptionTypeBadge exceptionType={ex.exceptionType} />
                    <InboundExceptionStatusBadge status={ex.status} />
                  </Group>
                  <Text fz={14} ff="monospace">
                    {dayjs(ex.createdAt).format("MM-DD HH:mm")}
                  </Text>
                </Group>

                <Text fz={14} mb={4}>
                  {ex.description}
                </Text>
                {(ex.expectedQuantity != null || ex.actualQuantity != null) && (
                  <Text fz={14} c="gray.8">
                    {t("inbound:detail.expectedActual", { expected: formatQuantity(ex.expectedQuantity), actual: formatQuantity(ex.actualQuantity) })}
                  </Text>
                )}
                {ex.resolutionNotes && (
                  <Text fz={14} c="gray.8" mt={4}>
                    {t("inbound:detail.resolution", { notes: ex.resolutionNotes })}
                  </Text>
                )}

                <Group gap="xs" mt="xs">
                  {start.applies && (
                    <DeliveryActionButton state={start} size="sm" variant="default" loading={exMutations.submitting} onClick={() => attempt(() => exMutations.start(inboundOrderId, ex.id))}>
                      {t("inbound:detail.start")}
                    </DeliveryActionButton>
                  )}
                  {repost.applies && (
                    <DeliveryActionButton state={repost} size="sm" variant="default" loading={mutations.submitting} onClick={repostStock}>
                      {t("inbound:detail.repostStock")}
                    </DeliveryActionButton>
                  )}
                  {resolve.applies && resolveTarget !== ex.id && (
                    <DeliveryActionButton
                      state={resolve}
                      size="sm"
                      variant="default"
                      onClick={() => {
                        setResolveTarget(ex.id)
                        setCloseTarget(null)
                      }}
                    >
                      {t("inbound:detail.resolve")}
                    </DeliveryActionButton>
                  )}
                  {close.applies && closeTarget !== ex.id && (
                    <DeliveryActionButton
                      state={close}
                      size="sm"
                      variant="default"
                      onClick={() => {
                        setCloseTarget(ex.id)
                        setResolveTarget(null)
                      }}
                    >
                      {t("inbound:detail.closeException")}
                    </DeliveryActionButton>
                  )}
                </Group>

                {resolveTarget === ex.id && (
                  <Stack gap="xs" mt="xs">
                    <Textarea placeholder={t("inbound:detail.resolveNotesPlaceholder")} value={resolveNotes} onChange={(e) => setResolveNotes(e.currentTarget.value)} rows={2} />
                    <Group gap="xs">
                      <Button
                        size="sm"
                        disabled={resolveNotes.trim().length < 3}
                        loading={exMutations.submitting}
                        onClick={() =>
                          attempt(async () => {
                            await exMutations.resolve(inboundOrderId, ex.id, resolveNotes)
                            setResolveNotes("")
                            setResolveTarget(null)
                          })
                        }
                      >
                        {t("inbound:detail.confirm")}
                      </Button>
                      <Button size="sm" variant="subtle" onClick={() => setResolveTarget(null)}>
                        {t("inbound:detail.back")}
                      </Button>
                    </Group>
                  </Stack>
                )}

                {closeTarget === ex.id && (
                  <Stack gap="xs" mt="xs">
                    <Textarea placeholder={t("inbound:detail.closeNotesPlaceholder")} value={closeNotes} onChange={(e) => setCloseNotes(e.currentTarget.value)} rows={2} />
                    <Group gap="xs">
                      <Button
                        size="sm"
                        loading={exMutations.submitting}
                        onClick={() =>
                          attempt(async () => {
                            await exMutations.close(inboundOrderId, ex.id, closeNotes || undefined)
                            setCloseNotes("")
                            setCloseTarget(null)
                          })
                        }
                      >
                        {t("inbound:detail.closeException")}
                      </Button>
                      <Button size="sm" variant="subtle" onClick={() => setCloseTarget(null)}>
                        {t("inbound:detail.back")}
                      </Button>
                    </Group>
                  </Stack>
                )}
              </Paper>
            )
          })}
        </Stack>
      )}

      {(exMutations.error || mutations.error) && (
        <Text fz={14} fw={600} mt="xs">
          {translateBackendError(exMutations.error ?? mutations.error)}
        </Text>
      )}
    </Paper>
  )
}
